import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API, mediaUrl, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { AnnotatedPlayer } from "../components/AnnotatedPlayer";
import { CoachRecordingPlayer } from "../components/CoachRecordingPlayer";
import { CoachStudio } from "../components/CoachStudio";

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return d.toLocaleDateString();
}

export default function VideoDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [video, setVideo] = useState(null);
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [err, setErr] = useState("");
  const [posting, setPosting] = useState(false);
  const [showStudio, setShowStudio] = useState(false);

  const load = useCallback(async () => {
    try {
      const [v, c] = await Promise.all([
        api.get(`/videos/${id}`),
        api.get(`/videos/${id}/comments`),
      ]);
      setVideo(v.data); setComments(c.data);
    } catch (e) {
      console.error("Failed to load video:", e);
      setVideo(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { nav("/login"); return; }
    if (!body.trim()) return;
    setPosting(true); setErr("");
    try {
      const { data } = await api.post(`/videos/${id}/comments`, { body });
      setComments((c) => [...c, data]);
      setBody("");
    } catch (e) { setErr(formatApiError(e)); }
    finally { setPosting(false); }
  };

  const removeComment = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/comments/${cid}`);
      setComments((c) => c.filter((x) => x.comment_id !== cid));
    } catch (e) {
      console.error("Failed to delete comment:", e);
    }
  };

  const removeVideo = async () => {
    if (!window.confirm("Delete this swing video?")) return;
    try {
      await api.delete(`/videos/${id}`);
      nav("/feed");
    } catch (e) {
      console.error("Failed to delete video:", e);
    }
  };

  const togglePick = async () => {
    try {
      const { data } = await api.post(`/videos/${id}/coach-pick`, { pick: !video.is_coach_pick });
      setVideo((v) => ({ ...v, is_coach_pick: data.is_coach_pick }));
    } catch (e) {
      console.error("Coach pick toggle failed:", e);
    }
  };

  const togglePublish = async () => {
    try {
      const { data } = await api.post(`/videos/${id}/publish`, { published: !video.is_published });
      setVideo((v) => ({ ...v, is_published: data.is_published }));
    } catch (e) {
      console.error("Publish toggle failed:", e);
    }
  };

  if (video === null) return <div className="min-h-screen pt-32 px-6 text-white/50">Loading...</div>;
  if (video === false) return (
    <div className="min-h-screen pt-32 px-6 text-center" data-testid="video-not-found">
      <h1 className="font-display text-5xl">Swing not found.</h1>
      <Link to="/feed" className="btn-primary mt-8 inline-block">Back to Feed</Link>
    </div>
  );

  const canDeleteVideo = user && (user.id === video.user_id || user.role === "admin");

  return (
    <div data-testid="video-detail-page" className="min-h-screen pt-28 pb-24 px-6">
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-[1.4fr_1fr] gap-10">
        {/* Video */}
        <div>
          {showStudio ? (
            <CoachStudio
              videoId={id}
              videoUrl={`${API}/videos/${id}/file`}
              onCancel={() => setShowStudio(false)}
              onDone={(r) => { setVideo((v) => ({ ...v, coach_response: r, has_response: true })); setShowStudio(false); }}
            />
          ) : video.coach_response?.recording_url ? (
            <CoachRecordingPlayer
              recordingUrl={mediaUrl(video.coach_response.recording_url)}
              text={video.coach_response.text}
              coachName={video.coach_response.coach_name}
            />
          ) : video.coach_response ? (
            <AnnotatedPlayer
              videoUrl={`${API}/videos/${id}/file`}
              voiceoverUrl={mediaUrl(video.coach_response.voiceover_url)}
              annotations={video.coach_response.annotations || []}
              text={video.coach_response.text}
              coachName={video.coach_response.coach_name}
            />
          ) : (
            <div className="bg-black aspect-video border border-white/10">
              <video data-testid="video-player" src={`${API}/videos/${id}/file`} controls className="w-full h-full" />
            </div>
          )}
          {user?.role === "admin" && !showStudio && (
            <button onClick={() => setShowStudio(true)} data-testid="open-studio-btn" className="btn-ghost w-full mt-3 text-sm">
              {video.coach_response ? "Edit Response" : "Open Response Studio"}
            </button>
          )}
          <div className="mt-6 flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                {mediaUrl(video.author_avatar_url) ? (
                  <img src={mediaUrl(video.author_avatar_url)} alt="" className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <span className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-[#050A08]"
                        style={{ background: video.author_avatar || "#C9A227" }}>
                    {(video.author_name || "G").slice(0,1).toUpperCase()}
                  </span>
                )}
                <div>
                  <div className="text-white font-semibold" data-testid="video-author">{video.author_name}</div>
                  <div className="text-white/40 text-xs">{timeAgo(video.created_at)}</div>
                </div>
                <span data-testid="video-visibility" className={`overline ml-2 ${video.is_published ? "text-[#C9A227]" : "text-white/40"}`}>
                  {video.is_published ? "● Public" : "● Private"}
                </span>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl mt-6" data-testid="video-title">{video.title}</h1>
              {video.description && <p className="text-white/70 mt-4 max-w-2xl whitespace-pre-line">{video.description}</p>}
            </div>
            {canDeleteVideo && (
              <div className="flex flex-col gap-2 items-end">
                <button onClick={togglePublish} data-testid="video-publish-toggle"
                        className={`!py-2 !px-4 text-xs ${video.is_published ? "btn-primary" : "btn-ghost"}`}>
                  {video.is_published ? "On Public Feed" : "Publish to Feed"}
                </button>
                {user?.role === "admin" && (
                  <button onClick={togglePick} data-testid="video-coach-pick-toggle"
                          className={`!py-2 !px-4 text-xs ${video.is_coach_pick ? "btn-primary" : "btn-ghost"}`}>
                    {video.is_coach_pick ? "★ Coach Pick" : "☆ Set as Coach Pick"}
                  </button>
                )}
                <button onClick={removeVideo} data-testid="video-delete" className="btn-ghost !py-2 !px-4 text-xs">Delete</button>
              </div>
            )}
          </div>
        </div>

        {/* Comments */}
        <div className="bg-[#08100D] border border-white/5 p-6">
          <div className="overline">Coaching thread</div>
          <h2 className="font-display text-3xl mt-2">Comments</h2>

          <div data-testid="comments-list" className="mt-6 space-y-4 max-h-[460px] overflow-auto pr-2">
            {comments.length === 0 ? (
              <p className="text-white/40 text-sm">No notes yet. Drop the first thought.</p>
            ) : comments.map((c) => {
              const isCoach = c.author_role === "admin";
              return (
                <div key={c.comment_id}
                     data-testid={`comment-${c.comment_id}`}
                     className={isCoach ? "border-l-2 border-[#C9A227] bg-[#1B3A2B]/30 p-4" : "border-l border-white/10 p-4"}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-[#050A08]"
                            style={{ background: c.author_avatar || "#C9A227" }}>
                        {(c.author_name || "G").slice(0,1).toUpperCase()}
                      </span>
                      <span className="text-sm text-white">{c.author_name}</span>
                      {isCoach && <span className="overline" style={{color:"#C9A227"}}>Coach</span>}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-white/30 text-xs">{timeAgo(c.created_at)}</span>
                      {user && (user.id === c.user_id || user.role === "admin") && (
                        <button onClick={() => removeComment(c.comment_id)} data-testid={`comment-delete-${c.comment_id}`} className="text-white/30 hover:text-white text-xs">×</button>
                      )}
                    </div>
                  </div>
                  <div className={`text-sm whitespace-pre-line ${isCoach ? "text-white" : "text-white/85"}`}>{c.body}</div>
                </div>
              );
            })}
          </div>

          {user ? (
            <form onSubmit={submit} className="mt-6 space-y-3">
              <textarea data-testid="comment-input" rows={3} value={body} onChange={(e) => setBody(e.target.value)}
                        className="input-dark" placeholder={user.role === "admin" ? "Coach's note (highlighted in gold)..." : "Ask the coach a question..."} />
              {err && <div className="text-red-400 text-xs" data-testid="comment-error">{err}</div>}
              <button data-testid="comment-submit" disabled={posting || !body.trim()} className="btn-primary !py-3 !px-6 text-xs">
                {posting ? "Posting..." : user.role === "admin" ? "Reply as Coach" : "Post Comment"}
              </button>
            </form>
          ) : (
            <div className="mt-6 p-4 border border-white/10 text-sm text-white/60">
              <Link to="/login" className="text-[#C9A227]" data-testid="comment-login-prompt">Log in</Link> to join the thread.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
